import { create } from 'zustand';
import { eventsDB, deletedItemsDB, type CalendarEvent, type RSVP } from '../../lib/db';

interface EventsState {
  // State
  events: CalendarEvent[];
  selectedEventId: string | null;
  isLoading: boolean;
  error: string | null;
  currentSpaceId: string;
  selectedDate: Date;
  view: 'month' | 'week' | 'day' | 'agenda';

  // Actions
  loadEvents: (spaceId: string) => Promise<void>;
  loadEventsByDateRange: (spaceId: string, startDate: Date, endDate: Date) => Promise<void>;
  selectEvent: (eventId: string | null) => void;
  createEvent: (event: Partial<CalendarEvent>, userId: string) => Promise<CalendarEvent>;
  updateEvent: (eventId: string, updates: Partial<CalendarEvent>) => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
  setCurrentSpace: (spaceId: string) => void;
  setSelectedDate: (date: Date) => void;
  setView: (view: 'month' | 'week' | 'day' | 'agenda') => void;
  updateRSVP: (eventId: string, rsvp: RSVP) => Promise<void>;

  // Computed
  getSelectedEvent: () => CalendarEvent | undefined;
  getEventsForDate: (date: Date) => CalendarEvent[];
}

export const useEventsStore = create<EventsState>((set, get) => ({
  // Initial state
  events: [],
  selectedEventId: null,
  isLoading: false,
  error: null,
  currentSpaceId: 'default',
  selectedDate: new Date(),
  view: 'month',

  // Load all events for a space
  loadEvents: async (spaceId: string) => {
    try {
      set({ isLoading: true, error: null });
      const events = await eventsDB.getBySpace(spaceId);
      set({ events, isLoading: false, currentSpaceId: spaceId });
    } catch (error) {
      console.error('Failed to load events:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load events',
        isLoading: false,
      });
    }
  },

  // Load events by date range
  loadEventsByDateRange: async (spaceId: string, startDate: Date, endDate: Date) => {
    try {
      set({ isLoading: true, error: null });
      const events = await eventsDB.getByDateRange(
        spaceId,
        startDate.toISOString(),
        endDate.toISOString()
      );
      set({ events, isLoading: false, currentSpaceId: spaceId });
    } catch (error) {
      console.error('Failed to load events by date range:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load events',
        isLoading: false,
      });
    }
  },

  // Select an event
  selectEvent: (eventId: string | null) => {
    set({ selectedEventId: eventId });
  },

  // Create a new event
  createEvent: async (eventData: Partial<CalendarEvent>, userId: string) => {
    try {
      set({ isLoading: true, error: null });

      const newEvent = await eventsDB.create({
        spaceId: eventData.spaceId || get().currentSpaceId,
        title: eventData.title || 'Untitled Event',
        description: eventData.description,
        startTime: eventData.startTime || new Date().toISOString(),
        endTime: eventData.endTime || new Date().toISOString(),
        isAllDay: eventData.isAllDay || false,
        location: eventData.location,
        color: eventData.color,
        category: eventData.category,
        createdBy: userId,
        rsvpEnabled: eventData.rsvpEnabled || false,
        rsvpList: eventData.rsvpList || [],
        status: eventData.status || 'confirmed',
        visibility: eventData.visibility || 'private',
        syncStatus: 'pending',
        syncToGoogleCalendar: eventData.syncToGoogleCalendar || false,
        googleCalendarSyncStatus: null,
      });

      set((state) => ({
        events: [...state.events, newEvent],
        selectedEventId: newEvent.id,
        isLoading: false,
      }));

      return newEvent;
    } catch (error) {
      console.error('Failed to create event:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to create event',
        isLoading: false,
      });
      throw error;
    }
  },

  // Update an event
  updateEvent: async (eventId: string, updates: Partial<CalendarEvent>) => {
    try {
      const updatedEvent = await eventsDB.update(eventId, {
        ...updates,
        syncStatus: 'pending', // Mark for sync
      });

      set((state) => ({
        events: state.events.map((event) => (event.id === eventId ? updatedEvent : event)),
      }));
    } catch (error) {
      console.error('Failed to update event:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update event',
      });
      throw error;
    }
  },

  // Delete an event
  deleteEvent: async (eventId: string) => {
    try {
      // Get the event to check if it has a driveFileId
      const event = get().events.find((e) => e.id === eventId);

      // Delete from IndexedDB
      await eventsDB.delete(eventId);

      // Track deletion to prevent re-download from Drive
      await deletedItemsDB.add(eventId, 'event');

      // If event was synced to Drive, delete from Drive too
      if (event?.driveFileId) {
        try {
          const { defaultSyncEngine } = await import('../../lib/sync-engine');
          await defaultSyncEngine.deleteEventFromDrive(event.driveFileId, event.googleCalendarEventId);
        } catch (driveError) {
          console.error('Failed to delete from Google Drive:', driveError);
          // Continue anyway - local deletion succeeded and tracked
        }
      }

      set((state) => ({
        events: state.events.filter((e) => e.id !== eventId),
        selectedEventId: state.selectedEventId === eventId ? null : state.selectedEventId,
      }));
    } catch (error) {
      console.error('Failed to delete event:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to delete event',
      });
      throw error;
    }
  },

  // Set current space
  setCurrentSpace: (spaceId: string) => {
    set({ currentSpaceId: spaceId });
    get().loadEvents(spaceId);
  },

  // Set selected date
  setSelectedDate: (date: Date) => {
    set({ selectedDate: date });
  },

  // Set view
  setView: (view: 'month' | 'week' | 'day' | 'agenda') => {
    set({ view });
  },

  // Update RSVP for an event
  updateRSVP: async (eventId: string, rsvp: RSVP) => {
    const event = get().events.find((e) => e.id === eventId);
    if (!event) return;

    // Update or add RSVP
    const existingRSVPIndex = event.rsvpList.findIndex((r) => r.userId === rsvp.userId);
    const updatedRSVPList = [...event.rsvpList];

    if (existingRSVPIndex >= 0) {
      updatedRSVPList[existingRSVPIndex] = rsvp;
    } else {
      updatedRSVPList.push(rsvp);
    }

    await get().updateEvent(eventId, { rsvpList: updatedRSVPList });
  },

  // Get selected event
  getSelectedEvent: () => {
    const { events, selectedEventId } = get();
    return events.find((event) => event.id === selectedEventId);
  },

  // Get events for a specific date
  getEventsForDate: (date: Date) => {
    const { events } = get();
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD

    return events.filter((event) => {
      const eventDate = new Date(event.startTime).toISOString().split('T')[0];
      return eventDate === dateStr;
    });
  },
}));
